import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { RefreshCw, LogOut, Calendar, User, ChevronDown, X, FileText } from 'lucide-react';
import { Navigation } from './Navigation';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-tooltip p-3">
        <p className="font-mono text-xs text-[#C5C6C7] mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="font-mono text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Date Input Component
const DateInput = ({ label, value, onChange, min, max }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs text-[#C5C6C7] uppercase tracking-widest">{label}</label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      min={min}
      max={max}
      className="bg-[#1F2833] border border-[#2D3436] text-white text-sm px-3 py-2 font-mono focus:outline-none focus:border-[#66FCF1]"
    />
  </div>
);

// Skeleton Loader
const ChartSkeleton = () => (
  <div className="h-64 skeleton rounded"></div>
);

// Activity Card Component
const ActivityCard = ({ activity, index }) => (
  <div 
    className="bg-[#0B0C10] border border-[#2D3436] p-4 hover:border-[#66FCF1]/30 transition-colors"
    style={{ borderLeftColor: activity.color, borderLeftWidth: '3px' }}
  >
    <div className="flex items-center justify-between mb-2">
      <h4 className="font-mono text-sm text-white">{activity.activity}</h4>
      <span 
        className="text-xs font-mono px-2 py-0.5 rounded"
        style={{ backgroundColor: `${activity.color}20`, color: activity.color }}
      >
        {activity.pct_of_business_hours}%
      </span>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-xs text-[#C5C6C7]">Antal</p>
        <p className="font-mono text-xl" style={{ color: activity.color }}>{activity.count}</p>
      </div>
      <div>
        <p className="text-xs text-[#C5C6C7]">Timer</p>
        <p className="font-mono text-xl text-white">{activity.duration_hours}h</p>
      </div>
    </div>
  </div>
);

// Orders Detail Modal Component
const OrdersDetailModal = ({ isOpen, onClose, orders, summary, salespersonName, dateRange, loading }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="orders-detail-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#0B0C10] border border-[#2D3436] w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#2D3436]">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-[#66FCF1]" />
            <div>
              <h2 className="font-heading text-lg text-white">ORDREDETALJER</h2>
              <p className="text-xs text-[#C5C6C7] font-mono">{salespersonName} · {dateRange?.start} til {dateRange?.end}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-[#C5C6C7] hover:text-[#FF2E63] transition-colors"
            data-testid="close-orders-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-3 gap-4 p-4 bg-[#1F2833] border-b border-[#2D3436]">
            <div className="text-center">
              <p className="text-xs text-[#C5C6C7] uppercase">Total Ordrer</p>
              <p className="font-mono text-xl text-white">{summary.total_orders}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#C5C6C7] uppercase">Total Dækningsbeløb</p>
              <p className="font-mono text-xl text-[#22C55E]">{summary.total_coverage?.toLocaleString('da-DK')} kr</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#C5C6C7] uppercase">Gns. pr. Ordre</p>
              <p className="font-mono text-xl text-[#22C55E]">{summary.avg_coverage_per_order?.toLocaleString('da-DK')} kr</p>
            </div>
          </div>
        )}
        
        {/* Orders Table */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 text-[#66FCF1] animate-spin" />
            </div>
          ) : orders && orders.length > 0 ? (
            <table className="data-table w-full">
              <thead className="sticky top-0 bg-[#0B0C10]">
                <tr>
                  <th>Dato</th>
                  <th>Ordre ID</th>
                  <th className="text-right">Total (inkl. moms)</th>
                  <th className="text-right">Kostpris</th>
                  <th className="text-right">Net (ekskl. moms)</th>
                  <th className="text-right">Dækningsbeløb</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, i) => (
                  <tr key={i}>
                    <td className="font-mono">{order.order_date}</td>
                    <td className="font-mono text-xs">{order.order_increment_id || order.order_id || '-'}</td>
                    <td className="text-right font-mono">{order.grand_total?.toLocaleString('da-DK')} kr</td>
                    <td className="text-right font-mono text-[#C5C6C7]">{order.cost?.toLocaleString('da-DK')} kr</td>
                    <td className="text-right font-mono text-[#C5C6C7]">{order.net_total?.toLocaleString('da-DK')} kr</td>
                    <td className={`text-right font-mono ${order.coverage >= 0 ? 'text-[#22C55E]' : 'text-[#FF2E63]'}`}>
                      {order.coverage?.toLocaleString('da-DK')} kr
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center justify-center h-32 text-[#C5C6C7]">
              <p className="font-mono text-sm">Ingen ordrer fundet</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-[#2D3436] bg-[#1F2833]">
          <p className="text-xs text-[#C5C6C7] italic">
            Dækningsbeløb = (Total / 1,25) - Kostpris. Formlen fjerner 25% moms først, derefter trækkes kostprisen fra.
          </p>
        </div>
      </div>
    </div>
  );
};

export const EmployeeActivity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Date range state - default to last 12 weeks
  const today = new Date().toISOString().split('T')[0];
  const twelveWeeksAgo = new Date(Date.now() - 84 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(twelveWeeksAgo);
  const [endDate, setEndDate] = useState(today);
  
  // Data state
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [activityData, setActivityData] = useState(null);
  const [coverageData, setCoverageData] = useState(null);
  
  // Orders modal state
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [ordersData, setOrdersData] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Fetch employees on mount
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get('/dashboard/data', {
          params: { region: 'Hele landet', week_offset: 0, excluded_employees: '' }
        });
        const empList = (response.data.employees || [])
          .filter(emp => emp.employee_type !== 'Montør' && emp.employee_type !== 'Leder')
          .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
        setEmployees(empList);
      } catch (err) {
        console.error('Failed to fetch employees:', err);
        if (err.response?.status === 401) {
          navigate('/login');
        }
      }
    };
    fetchEmployees();
  }, [navigate]);

  // Fetch coverage data when employee or date changes
  const fetchCoverageData = useCallback(async (employeeName) => {
    if (!employeeName) return;
    
    try {
      const response = await api.get('/employee/coverage', {
        params: {
          salesperson_name: employeeName,
          start_date: startDate,
          end_date: endDate
        }
      });
      setCoverageData(response.data);
    } catch (err) {
      console.error('Failed to fetch coverage data:', err);
      setCoverageData(null);
    }
  }, [startDate, endDate]);

  // Fetch detailed orders for modal
  const fetchOrdersDetail = useCallback(async () => {
    if (!activityData?.employee?.name) return;
    
    setOrdersLoading(true);
    try {
      const response = await api.get('/employee/coverage/orders', {
        params: {
          salesperson_name: activityData.employee.name,
          start_date: startDate,
          end_date: endDate
        }
      });
      setOrdersData(response.data);
    } catch (err) {
      console.error('Failed to fetch orders detail:', err);
      setOrdersData(null);
    } finally {
      setOrdersLoading(false);
    }
  }, [activityData?.employee?.name, startDate, endDate]);

  // Open orders modal and fetch data
  const handleOpenOrdersModal = () => {
    setOrdersModalOpen(true);
    fetchOrdersDetail();
  };

  const fetchActivityData = useCallback(async () => {
    if (!selectedEmployee) return;
    
    setLoading(true);
    try {
      const response = await api.get('/employee/activities', {
        params: {
          email: selectedEmployee,
          start_date: startDate,
          end_date: endDate
        }
      });
      setActivityData(response.data);
      setError(null);
      
      // Also fetch coverage data using employee name
      if (response.data?.employee?.name) {
        fetchCoverageData(response.data.employee.name);
      }
    } catch (err) {
      console.error('Activity data fetch error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || 'Kunne ikke hente aktivitetsdata');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee, startDate, endDate, navigate, fetchCoverageData]);

  useEffect(() => {
    if (selectedEmployee) {
      fetchActivityData();
    }
  }, [selectedEmployee, fetchActivityData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchActivityData();
    if (activityData?.employee?.name) {
      await fetchCoverageData(activityData.employee.name);
    }
    setRefreshing(false);
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/login');
    }
  };

  // Quick date range presets (in weeks)
  const setWeekRange = (weeks) => {
    const end = new Date();
    const start = new Date(Date.now() - weeks * 7 * 24 * 60 * 60 * 1000);
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  };

  // Prepare data for pie chart
  const pieData = activityData?.activity_breakdown?.map(a => ({
    name: a.activity,
    value: a.duration_hours,
    color: a.color
  })) || [];

  // Prepare data for weekly stacked bar chart
  const weeklyChartData = activityData?.weekly_data?.map(week => {
    const data = { week_label: week.week_label };
    activityData.activity_breakdown.forEach(a => {
      data[a.activity] = week[`${a.activity_key}_duration`] ? Math.round(week[`${a.activity_key}_duration`] / 60 * 10) / 10 : 0;
    });
    return data;
  }) || [];

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="employee-activity">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 bg-[#121212]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1920px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Title */}
            <div className="flex items-center gap-4">
              <Navigation />
              <h1 className="text-xl font-semibold text-white">Medarbejder Aktivitet</h1>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3">
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={refreshing || !selectedEmployee}
                className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-white hover:border-[#1E6F7A] rounded-lg transition-colors disabled:opacity-50"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-[#B3261E] hover:border-[#B3261E] rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto px-4 md:px-6 py-6">
        {/* Employee Selector & Date Range */}
        <div className="bg-[#1A1A1A] rounded-lg p-6 mb-6 card-shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Employee Selector */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <User className="w-4 h-4 text-[#1E6F7A]" />
                <h3 className="text-sm text-white/60">
                  Vælg medarbejder
                </h3>
              </div>
              <div className="relative">
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="w-full bg-[#1F2833] border border-[#2D3436] text-white text-sm px-4 py-3 font-mono focus:outline-none focus:border-[#66FCF1] appearance-none cursor-pointer"
                  data-testid="employee-select"
                >
                  <option value="">-- Vælg medarbejder --</option>
                  {employees.map(emp => (
                    <option key={emp.email} value={emp.email}>
                      {emp.name || emp.email} ({emp.employee_type})
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C5C6C7] pointer-events-none" />
              </div>
            </div>

            {/* Date Range */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-4 h-4 text-[#66FCF1]" />
                <h3 className="font-heading text-sm text-[#C5C6C7] tracking-widest">
                  VÆLG PERIODE
                </h3>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <DateInput 
                  label="Fra" 
                  value={startDate} 
                  onChange={setStartDate}
                  max={endDate}
                />
                <DateInput 
                  label="Til" 
                  value={endDate} 
                  onChange={setEndDate}
                  min={startDate}
                  max={today}
                />
                <div className="flex gap-2">
                  <button onClick={() => setWeekRange(4)} className="px-3 py-2 text-xs font-mono border border-[#2D3436] text-[#C5C6C7] hover:border-[#66FCF1] hover:text-[#66FCF1] transition-colors">4 uger</button>
                  <button onClick={() => setWeekRange(12)} className="px-3 py-2 text-xs font-mono border border-[#2D3436] text-[#C5C6C7] hover:border-[#66FCF1] hover:text-[#66FCF1] transition-colors">12 uger</button>
                  <button onClick={() => setWeekRange(26)} className="px-3 py-2 text-xs font-mono border border-[#2D3436] text-[#C5C6C7] hover:border-[#66FCF1] hover:text-[#66FCF1] transition-colors">26 uger</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* No Employee Selected */}
        {!selectedEmployee && (
          <div className="bg-[#0B0C10] border border-[#2D3436] p-12 text-center">
            <User className="w-12 h-12 text-[#2D3436] mx-auto mb-4" />
            <p className="text-[#C5C6C7] font-mono">Vælg en medarbejder for at se aktivitetsdata</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="warning-banner flex items-center gap-3 mb-6">
            <span className="font-mono">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && selectedEmployee && (
          <div className="grid grid-cols-1 gap-6">
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
        )}

        {/* Activity Data */}
        {activityData && !loading && (
          <>
            {/* Employee Info & Summary */}
            <div className="bg-[#1F2833] border border-[#2D3436] p-6 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="font-heading text-xl text-white mb-1">{activityData.employee.name}</h2>
                  <p className="text-sm text-[#C5C6C7] font-mono">{activityData.employee.email}</p>
                  <p className="text-xs text-[#66FCF1] font-mono mt-1">{activityData.employee.type} · {activityData.employee.region}</p>
                </div>
                <div className="flex gap-6">
                  <div className="text-center">
                    <p className="text-xs text-[#C5C6C7] uppercase">Arbejdstid</p>
                    <p className="font-mono text-2xl text-[#66FCF1]">{activityData.summary.total_business_hours}h</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#C5C6C7] uppercase">Opmålinger</p>
                    <p className="font-mono text-2xl text-[#45A29E]">{activityData.summary.total_opmaling}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#C5C6C7] uppercase">KPT</p>
                    <p className="font-mono text-2xl text-[#FFD700]">{activityData.summary.overall_kpt}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#C5C6C7] uppercase">Uger</p>
                    <p className="font-mono text-2xl text-white">{activityData.summary.weeks_count}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* KPT Chart */}
            <div className="bg-[#0B0C10] border border-[#2D3436] p-6 mb-6">
              <h3 className="font-heading text-sm text-[#C5C6C7] tracking-widest mb-6">
                KPT (KUNDER PR. TIME) OVER TID
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={activityData.weekly_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3436" />
                  <XAxis 
                    dataKey="week_label" 
                    stroke="#C5C6C7" 
                    tick={{ fill: '#C5C6C7', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  />
                  <YAxis 
                    stroke="#C5C6C7" 
                    tick={{ fill: '#C5C6C7', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                    domain={[0, 'auto']}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="kpt" 
                    stroke="#FFD700" 
                    strokeWidth={3}
                    dot={{ fill: '#FFD700', strokeWidth: 0, r: 4 }}
                    name="KPT"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Dækningsbeløb Chart */}
            {coverageData && coverageData.weekly_data.length > 0 && (
              <div className="bg-[#0B0C10] border border-[#2D3436] p-6 mb-6">
                <h3 className="font-heading text-sm text-[#C5C6C7] tracking-widest mb-6">
                  GENNEMSNITLIGT DÆKNINGSBELØB PR. ORDRE
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={coverageData.weekly_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3436" vertical={false} />
                    <XAxis 
                      dataKey="week_label" 
                      stroke="#C5C6C7" 
                      tick={{ fill: '#C5C6C7', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                    />
                    <YAxis 
                      stroke="#C5C6C7" 
                      tick={{ fill: '#C5C6C7', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                      tickFormatter={(value) => `${value.toLocaleString('da-DK')} kr`}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value.toLocaleString('da-DK')} kr`, 'Gns. Dækningsbeløb']}
                      contentStyle={{ backgroundColor: '#1F2833', border: '1px solid #2D3436' }}
                      labelStyle={{ color: '#C5C6C7' }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="avg_coverage_per_order" 
                      fill="#22C55E" 
                      name="Gns. Dækningsbeløb"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-xs text-[#C5C6C7] mt-4 italic">
                  * Dækningsbeløb indeholder monteringspriserne
                </p>
                {/* Summary - Clickable */}
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[#2D3436]">
                  <button 
                    onClick={handleOpenOrdersModal}
                    className="text-center p-2 rounded hover:bg-[#1F2833] transition-colors cursor-pointer group"
                    data-testid="total-orders-btn"
                  >
                    <p className="text-xs text-[#C5C6C7] group-hover:text-[#66FCF1]">Total Ordrer</p>
                    <p className="font-mono text-lg text-white group-hover:text-[#66FCF1] underline decoration-dotted underline-offset-4">
                      {coverageData.summary.total_orders}
                    </p>
                    <p className="text-xs text-[#66FCF1] opacity-0 group-hover:opacity-100 transition-opacity mt-1">Klik for detaljer</p>
                  </button>
                  <button 
                    onClick={handleOpenOrdersModal}
                    className="text-center p-2 rounded hover:bg-[#1F2833] transition-colors cursor-pointer group"
                    data-testid="total-coverage-btn"
                  >
                    <p className="text-xs text-[#C5C6C7] group-hover:text-[#66FCF1]">Total Dækningsbeløb</p>
                    <p className="font-mono text-lg text-[#22C55E] group-hover:text-[#66FCF1] underline decoration-dotted underline-offset-4">
                      {coverageData.summary.total_coverage.toLocaleString('da-DK')} kr
                    </p>
                    <p className="text-xs text-[#66FCF1] opacity-0 group-hover:opacity-100 transition-opacity mt-1">Klik for detaljer</p>
                  </button>
                  <div className="text-center p-2">
                    <p className="text-xs text-[#C5C6C7]">Gns. pr. Ordre</p>
                    <p className="font-mono text-lg text-[#22C55E]">{coverageData.summary.avg_coverage_per_order.toLocaleString('da-DK')} kr</p>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {activityData.activity_breakdown.slice(0, 8).map((activity, i) => (
                <ActivityCard key={activity.activity_key} activity={activity} index={i} />
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Pie Chart - Activity Distribution */}
              <div className="bg-[#0B0C10] border border-[#2D3436] p-6">
                <h3 className="font-heading text-sm text-[#C5C6C7] tracking-widest mb-6">
                  TIDSFORDELING
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#C5C6C7' }}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Bar Chart - Activity Counts */}
              <div className="bg-[#0B0C10] border border-[#2D3436] p-6">
                <h3 className="font-heading text-sm text-[#C5C6C7] tracking-widest mb-6">
                  ANTAL AKTIVITETER
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={activityData.activity_breakdown} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3436" horizontal={true} vertical={false} />
                    <XAxis type="number" stroke="#C5C6C7" tick={{ fill: '#C5C6C7', fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                    <YAxis type="category" dataKey="activity" stroke="#C5C6C7" tick={{ fill: '#C5C6C7', fontSize: 10, fontFamily: 'JetBrains Mono' }} width={100} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Antal">
                      {activityData.activity_breakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Weekly Trend Chart */}
            <div className="bg-[#0B0C10] border border-[#2D3436] p-6 mb-6">
              <h3 className="font-heading text-sm text-[#C5C6C7] tracking-widest mb-6">
                UGENTLIG AKTIVITET (TIMER)
              </h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={weeklyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3436" vertical={false} />
                  <XAxis 
                    dataKey="week_label" 
                    stroke="#C5C6C7" 
                    tick={{ fill: '#C5C6C7', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  />
                  <YAxis 
                    stroke="#C5C6C7" 
                    tick={{ fill: '#C5C6C7', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                    tickFormatter={(value) => `${value}h`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {activityData.activity_breakdown.map((a, i) => (
                    <Bar key={a.activity_key} dataKey={a.activity} stackId="a" fill={a.color} name={a.activity} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Data Table */}
            <div className="bg-[#0B0C10] border border-[#2D3436] p-6">
              <h3 className="font-heading text-sm text-[#C5C6C7] tracking-widest mb-6">
                AKTIVITETSOVERSIGT
              </h3>
              <div className="overflow-x-auto">
                <table className="data-table w-full">
                  <thead>
                    <tr>
                      <th>Aktivitet</th>
                      <th className="text-right">Antal</th>
                      <th className="text-right">Timer</th>
                      <th className="text-right">% af Arbejdstid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityData.activity_breakdown.map((activity, i) => (
                      <tr key={i}>
                        <td>
                          <span className="inline-block w-3 h-3 mr-2 rounded-sm" style={{ backgroundColor: activity.color }}></span>
                          {activity.activity}
                        </td>
                        <td className="text-right font-mono">{activity.count}</td>
                        <td className="text-right font-mono">{activity.duration_hours}h</td>
                        <td className="text-right font-mono text-[#66FCF1]">{activity.pct_of_business_hours}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#2D3436] py-4 mt-8">
        <div className="max-w-[1920px] mx-auto px-4 md:px-6">
          <p className="text-center text-[#C5C6C7] text-xs font-mono">
            GARDINBUSSEN OPERATIONS · MEDARBEJDER AKTIVITET · {new Date().getFullYear()}
          </p>
        </div>
      </footer>
      
      {/* Orders Detail Modal */}
      <OrdersDetailModal
        isOpen={ordersModalOpen}
        onClose={() => setOrdersModalOpen(false)}
        orders={ordersData?.orders}
        summary={ordersData?.summary}
        salespersonName={activityData?.employee?.name}
        dateRange={ordersData?.date_range || { start: startDate, end: endDate }}
        loading={ordersLoading}
      />
    </div>
  );
};
